var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines */
import * as React from 'react';
import { useUID } from 'react-uid';
import cn from 'classnames';
import { usePrevious } from '@cb/react-hooks';
import { useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react';
import { ICON_LEFT, ICON_RIGHT, } from './types';
import { SimpleAccordionContext, SimpleAccordionItemContext } from './contexts';
import '@cb/apricot/CBAccordion';
import SimpleAccordionControls from './simpleAccordionControls';
function extendEvent(handler, localHandler) {
    return (event) => {
        handler && handler(event);
        if (!event.defaultPrevented) {
            return localHandler(event);
        }
        return null;
    };
}
/**
 * Simple Accordion
 */
export const SimpleAccordion = React.forwardRef((_a, ref) => {
    var { children, onItemChange, className, index, id, single = true, expandable = false, shouldReindexChildren, title, controls } = _a, props = __rest(_a, ["children", "onItemChange", "className", "index", "id", "single", "expandable", "shouldReindexChildren", "title", "controls"]);
    const [selectedIndex, setSelectedIndex] = React.useState({});
    const [forceReindex, setForceReindex] = useState(0);
    const uid = useUID();
    const accordionId = id || `sa_${uid}`;
    const currentIndex = useRef(-1);
    const previousChildren = usePrevious(children) || [];
    useEffect(() => {
        if (shouldReindexChildren &&
            shouldReindexChildren(React.Children.toArray(previousChildren), React.Children.toArray(children))) {
            currentIndex.current = -1;
            setForceReindex(new Date().getTime());
        }
    }, [children]);
    // Revert forceReindex once handled
    useEffect(() => {
        if (forceReindex) {
            setForceReindex(0);
        }
    }, [forceReindex]);
    const selectPanel = (ind, open) => {
        setSelectedIndex(previous => {
            // no index passed return what we basically had
            if (ind === undefined || ind < 0) {
                return single ? {} : previous;
            }
            const openFlag = open === undefined ? !previous[ind] : open;
            onItemChange && onItemChange(ind, openFlag);
            // index was given - extend the selection object
            return Object.assign(Object.assign({}, (single && openFlag ? {} : previous)), { [ind]: openFlag });
        });
    };
    const registerIndex = (refObject) => {
        // Panel default ref value is -1
        if (refObject.current === -1) {
            // eslint-disable-next-line no-plusplus,no-param-reassign
            refObject.current = ++currentIndex.current;
        }
        return refObject.current;
    };
    const isSelected = (ind) => {
        return selectedIndex[ind];
    };
    const context = useMemo(() => ({
        accordionId,
        expandable,
        registerIndex,
        isSelected,
        selectPanel,
        forceReindex,
        panelCount: currentIndex.current + 1,
    }), [accordionId, selectedIndex, isSelected]);
    useEffect(() => {
        selectPanel(index, true);
    }, [index, single]);
    return (React.createElement(SimpleAccordionContext.Provider, { value: context },
        React.createElement("div", Object.assign({ className: cn('cb-accordion cb-accordion-simple', expandable && 'cb-accordion-expandable', className), ref: ref, "data-cbtrack-comp": "apricot-react:accordion" }, props),
            (title || controls) && React.createElement(SimpleAccordionControls, { title: title, controls: !single && controls }),
            React.createElement("div", { className: "cb-accordion-panels" }, children))));
});
SimpleAccordion.displayName = 'SimpleAccordion';
/**
 * Accordion Item
 */
export const SimpleAccordionItem = React.forwardRef((_a, ref) => {
    var { children, className, panelOverlay, open } = _a, props = __rest(_a, ["children", "className", "panelOverlay", "open"]);
    const { selectPanel, isSelected, accordionId, expandable, registerIndex, forceReindex } = React.useContext(SimpleAccordionContext);
    const indexRef = React.useRef(-1);
    const index = registerIndex(indexRef);
    const selected = isSelected(index);
    const context = useMemo(() => ({
        panelId: `accordion-${accordionId}-panel-${index}`,
        buttonId: `accordion-${accordionId}-button-${index}`,
        selected,
        expandable,
        onSelect: () => selectPanel(index),
    }), [accordionId, index, selected]);
    useEffect(() => {
        if (forceReindex) {
            indexRef.current = -1;
        }
    }, [forceReindex]);
    useEffect(() => {
        selectPanel(index, !!open);
    }, [index, open]);
    return (React.createElement(SimpleAccordionItemContext.Provider, { value: context },
        React.createElement("div", Object.assign({ className: cn('cb-accordion-container', panelOverlay && 'cb-accordion-panel-overflow', className) }, props, { ref: ref }), children)));
});
SimpleAccordionItem.displayName = 'SimpleAccordionItem';
/**
 * Accordion Icon
 */
export const SimpleAccordionIcon = ({ disabled, disabledLabel, iconPosition }) => {
    return (React.createElement("div", { role: disabled ? 'img' : undefined, "aria-label": disabled && disabledLabel ? disabledLabel : undefined, className: cn('cb-accordion-icon', iconPosition === ICON_LEFT && 'cb-margin-right-8', iconPosition === ICON_RIGHT && 'cb-margin-left-8') },
        React.createElement("span", { className: "line top" }),
        React.createElement("span", { className: "line bottom" })));
};
/**
 * Accordion Button
 */
export const SimpleAccordionHeader = React.forwardRef((_a, ref) => {
    var { children, onClick, className, disabled = false, inTabOrder = false, disabledLabel = 'locked', iconPosition = ICON_RIGHT } = _a, props = __rest(_a, ["children", "onClick", "className", "disabled", "inTabOrder", "disabledLabel", "iconPosition"]);
    const { panelId, buttonId, selected, onSelect } = React.useContext(SimpleAccordionItemContext);
    // accordion should open with enter and space
    const a11YKeydown = (e) => {
        const k = e.which || e.keyCode;
        // space or enter
        if (k === 32 || k === 13) {
            e.preventDefault();
            onSelect();
        }
    };
    return (React.createElement("a", Object.assign({ tabIndex: disabled && !inTabOrder ? -1 : 0, role: "button", className: cn('cb-accordion-heading', selected && 'cb-active', disabled && 'cb-disabled', iconPosition === ICON_LEFT && 'cb-accordion-left-icon', className) }, props, { onClick: !disabled ? extendEvent(onClick, onSelect) : undefined, onKeyDown: !disabled ? a11YKeydown : undefined, "aria-expanded": selected || false, "aria-controls": panelId, "aria-disabled": disabled ? true : undefined, id: buttonId, ref: ref }),
        React.createElement("div", { className: "cb-accordion-heading-title" },
            iconPosition === ICON_LEFT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel })),
            children,
            iconPosition === ICON_RIGHT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel })))));
});
SimpleAccordionHeader.displayName = 'SimpleAccordionHeader';
/**
 * Accordion Trigger - Expandable
 */
export const SimpleAccordionTrigger = React.forwardRef((_a, ref) => {
    var { defaultOpen = false, onClick, className, disabled = false, inTabOrder = false, disabledLabel = 'locked', closedLabel = 'See More', openLabel = 'See Less', iconPosition = ICON_RIGHT } = _a, props = __rest(_a, ["defaultOpen", "onClick", "className", "disabled", "inTabOrder", "disabledLabel", "closedLabel", "openLabel", "iconPosition"]);
    const { panelId, buttonId, selected, onSelect } = React.useContext(SimpleAccordionItemContext);
    const [label, setLabel] = useState(defaultOpen ? openLabel : closedLabel);
    // accordion should open with enter and space
    const a11YKeydown = (e) => {
        const k = e.which || e.keyCode;
        // space or enter
        if (k === 32 || k === 13) {
            e.preventDefault();
            onSelect();
        }
    };
    useEffect(() => {
        setLabel(selected ? openLabel : closedLabel);
    }, [selected, openLabel]);
    return (React.createElement("a", Object.assign({ tabIndex: disabled && !inTabOrder ? -1 : 0, role: "button", className: cn('cb-accordion-trigger', 'cb-link-blue', selected && 'cb-active', disabled && 'cb-disabled', iconPosition === ICON_LEFT && 'cb-accordion-left-icon', className) }, props, { onClick: !disabled ? extendEvent(onClick, onSelect) : undefined, onKeyDown: !disabled ? a11YKeydown : undefined, "aria-expanded": selected || false, "aria-controls": panelId, "aria-disabled": disabled ? true : undefined, id: buttonId, ref: ref }),
        iconPosition === ICON_LEFT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel, iconPosition: iconPosition })),
        label,
        iconPosition === ICON_RIGHT && (React.createElement(SimpleAccordionIcon, { disabled: disabled, disabledLabel: disabledLabel, iconPosition: iconPosition }))));
});
SimpleAccordionTrigger.displayName = 'SimpleAccordionTrigger';
/**
 * Accordion Panel
 */
export const SimpleAccordionPanel = React.forwardRef((_a, ref) => {
    var { children, className, offScreen, ariaLabelledbyID, classNameContent } = _a, props = __rest(_a, ["children", "className", "offScreen", "ariaLabelledbyID", "classNameContent"]);
    const { buttonId, panelId, selected, expandable } = React.useContext(SimpleAccordionItemContext);
    const divRef = useRef(null);
    useEffect(() => {
        var _a;
        if (expandable && selected) {
            divRef && ((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.focus());
        }
    }, [selected, expandable]);
    useImperativeHandle(ref, () => divRef.current);
    return (React.createElement("div", Object.assign({ role: "region", className: cn('cb-accordion-panel', selected && 'cb-in', className) }, props, { id: panelId, "aria-labelledby": !offScreen ? ariaLabelledbyID || buttonId : undefined, "aria-label": offScreen || undefined, "aria-hidden": !selected, hidden: !selected, tabIndex: expandable ? -1 : undefined, ref: divRef }),
        React.createElement("div", { className: cn('cb-accordion-panel-content', classNameContent) }, children)));
});
SimpleAccordionPanel.displayName = 'SimpleAccordionPanel';
//# sourceMappingURL=simpleAccordion.js.map